// ============================================================================
// Compliance Onboarding Wizard
// Extended 12-step onboarding flow with full HRIS compliance integration
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Phone,
  MapPin,
  Heart,
  FileText,
  Landmark,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Building2,
  Calendar,
  Shield,
  Save,
  Loader2,
  ClipboardCheck,
  GraduationCap,
  Scale,
  FileCheck,
  UserCheck,
  Award,
  Sparkles,
  PartyPopper,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { db } from '@/lib/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { useAuthStore } from '@/store/authStore';
import { getStateDropdownOptions } from '@/lib/stateTaxData';

// Import compliance components
import FormI9 from './compliance/FormI9';
import FormW4 from './compliance/FormW4';
import FormDE4 from './compliance/FormDE4';
import WageTheftNotice from './compliance/WageTheftNotice';
import CaliforniaNoticesViewer from './compliance/CaliforniaNoticesViewer';
import PolicyAcknowledgment from './compliance/PolicyAcknowledgment';
import ESignature from './compliance/ESignature';
import { COMPANY_POLICIES } from '@/lib/policyTemplates';

// Alias for compatibility
const POLICY_TEMPLATES = COMPANY_POLICIES;

// ============================================================================
// ONBOARDING STEPS CONFIGURATION (12 Steps)
// ============================================================================

const ONBOARDING_STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Sparkles, description: 'Get started with onboarding', category: 'intro' },
  { id: 'personal', title: 'Personal Info', icon: User, description: 'Basic personal details', category: 'personal' },
  { id: 'contact', title: 'Contact & Address', icon: MapPin, description: 'Your address and contact', category: 'personal' },
  { id: 'emergency', title: 'Emergency Contact', icon: Heart, description: 'Emergency contact details', category: 'personal' },
  { id: 'i9', title: 'Form I-9', icon: UserCheck, description: 'Employment eligibility', category: 'compliance' },
  { id: 'w4', title: 'Form W-4', icon: FileCheck, description: 'Federal tax withholding', category: 'compliance' },
  { id: 'de4', title: 'Form DE-4', icon: FileText, description: 'California state tax', category: 'compliance' },
  { id: 'wtpn', title: 'Wage Notice', icon: Scale, description: 'Wage theft prevention', category: 'compliance' },
  { id: 'notices', title: 'CA Notices', icon: Building2, description: 'California workplace notices', category: 'compliance' },
  { id: 'policies', title: 'Company Policies', icon: ClipboardCheck, description: 'Policy acknowledgments', category: 'policies' },
  { id: 'bank', title: 'Direct Deposit', icon: Landmark, description: 'Bank account for pay', category: 'payment' },
  { id: 'complete', title: 'Complete!', icon: PartyPopper, description: 'You\'re all set', category: 'complete' },
];

// US States for dropdown
const US_STATES = getStateDropdownOptions();

// ============================================================================
// STEP PROGRESS INDICATOR
// ============================================================================

function StepProgressIndicator({ steps, currentStep, completedSteps, onStepClick }) {
  const categories = [
    { id: 'intro', label: 'Welcome', color: 'blue' },
    { id: 'personal', label: 'Personal Info', color: 'green' },
    { id: 'compliance', label: 'Compliance Forms', color: 'amber' },
    { id: 'policies', label: 'Policies', color: 'purple' },
    { id: 'payment', label: 'Payment', color: 'blue' },
    { id: 'complete', label: 'Complete', color: 'green' },
  ];

  return (
    <div className="hidden lg:block w-72 flex-shrink-0">
      <div className="sticky top-6 space-y-6">
        {/* Progress */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Your Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={(completedSteps.length / steps.length) * 100} className="h-2 mb-2" />
            <p className="text-sm text-gray-500">
              {completedSteps.length} of {steps.length} steps complete
            </p>
          </CardContent>
        </Card>

        {/* Steps */}
        <Card>
          <CardContent className="pt-6">
            <ScrollArea className="h-[calc(100vh-300px)]">
              <div className="space-y-1">
                {steps.map((step, index) => {
                  const isCompleted = completedSteps.includes(step.id);
                  const isCurrent = index === currentStep;
                  const isClickable = index <= currentStep || completedSteps.includes(steps[index - 1]?.id);

                  return (
                    <button
                      key={step.id}
                      onClick={() => isClickable && onStepClick(index)}
                      disabled={!isClickable}
                      className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all ${
                        isCurrent 
                          ? 'bg-blue-50 border border-blue-200' 
                          : isCompleted
                            ? 'bg-green-50 hover:bg-green-100'
                            : isClickable
                              ? 'hover:bg-gray-50'
                              : 'opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isCurrent
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <step.icon className="h-4 w-4" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${
                          isCurrent ? 'text-blue-700' : isCompleted ? 'text-green-700' : 'text-gray-700'
                        }`}>
                          {step.title}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{step.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================================================
// MOBILE STEP INDICATOR
// ============================================================================

function MobileStepIndicator({ steps, currentStep, completedSteps }) {
  const step = steps[currentStep];
  
  return (
    <div className="lg:hidden mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center">
            <step.icon className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">{step.title}</p>
            <p className="text-sm text-gray-500">Step {currentStep + 1} of {steps.length}</p>
          </div>
        </div>
        <Badge variant="outline">
          {Math.round((completedSteps.length / steps.length) * 100)}%
        </Badge>
      </div>
      <Progress value={(currentStep / (steps.length - 1)) * 100} className="h-2" />
    </div>
  );
}

// ============================================================================
// WELCOME STEP
// ============================================================================

function WelcomeStep({ employee, onStart }) {
  const firstName = employee?.profile?.firstName || employee?.firstName || 'New Team Member';
  return (
    <div className="text-center py-12">
      <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 mx-auto mb-6 flex items-center justify-center">
        <Sparkles className="h-10 w-10 text-white" />
      </div>
      <h1 className="text-3xl font-bold text-gray-900 mb-3">
        Welcome to the Team, {firstName}!
      </h1>
      <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
        We're excited to have you join us. Let's get you set up with everything you need.
        This onboarding process will take about 20-30 minutes.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6 text-center">
            <User className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <p className="font-medium text-blue-900">Personal Info</p>
            <p className="text-sm text-blue-700">Your basic details</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-6 text-center">
            <FileCheck className="h-8 w-8 text-amber-600 mx-auto mb-2" />
            <p className="font-medium text-amber-900">Compliance Forms</p>
            <p className="text-sm text-amber-700">Tax & legal documents</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-6 text-center">
            <Landmark className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="font-medium text-green-900">Direct Deposit</p>
            <p className="text-sm text-green-700">Get paid seamlessly</p>
          </CardContent>
        </Card>
      </div>

      <Button size="lg" onClick={onStart} className="px-8">
        Let's Get Started
        <ChevronRight className="h-5 w-5 ml-2" />
      </Button>
    </div>
  );
}

// ============================================================================
// COMPLETION STEP
// ============================================================================

function CompletionStep({ employee, onFinish }) {
  const firstName = employee?.profile?.firstName || employee?.firstName || 'Team Member';
  return (
    <div className="text-center py-12">
      <div className="w-24 h-24 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 mx-auto mb-6 flex items-center justify-center">
        <PartyPopper className="h-12 w-12 text-white" />
      </div>
      <h1 className="text-3xl font-bold text-gray-900 mb-3">
        You're All Set, {firstName}! 🎉
      </h1>
      <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
        Congratulations! You've completed all your onboarding tasks. Your manager and HR 
        have been notified, and you're ready to get started.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg mx-auto mb-8">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-6">
            <CheckCircle className="h-8 w-8 text-green-600 mb-2" />
            <p className="font-medium text-green-900">All Forms Complete</p>
            <p className="text-sm text-green-700">I-9, W-4, DE-4, Policies</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <Shield className="h-8 w-8 text-blue-600 mb-2" />
            <p className="font-medium text-blue-900">100% Compliant</p>
            <p className="text-sm text-blue-700">Ready for payroll</p>
          </CardContent>
        </Card>
      </div>

      <Alert className="max-w-lg mx-auto mb-8 bg-yellow-50 border-yellow-200">
        <AlertCircle className="h-4 w-4 text-yellow-600" />
        <AlertTitle className="text-yellow-800">Next Steps</AlertTitle>
        <AlertDescription className="text-yellow-700">
          <ul className="text-left mt-2 space-y-1">
            <li>• Check your email for IT setup instructions</li>
            <li>• Your manager will reach out about your first day</li>
            <li>• Bring your I-9 documents on your first day for verification</li>
          </ul>
        </AlertDescription>
      </Alert>

      <Button size="lg" onClick={onFinish} className="px-8 bg-green-600 hover:bg-green-700">
        Go to Dashboard
        <ChevronRight className="h-5 w-5 ml-2" />
      </Button>
    </div>
  );
}

// ============================================================================
// PERSONAL INFO STEP
// ============================================================================

function PersonalInfoStep({ formData, updateFormData, errors }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-1">Personal Information</h2>
        <p className="text-gray-500">Let's start with your basic details</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="firstName">First Name *</Label>
          <Input
            id="firstName"
            value={formData.personal.firstName}
            onChange={(e) => updateFormData('personal', 'firstName', e.target.value)}
            className={errors['personal.firstName'] ? 'border-red-500' : ''}
          />
          {errors['personal.firstName'] && (
            <p className="text-sm text-red-500 mt-1">{errors['personal.firstName']}</p>
          )}
        </div>
        <div>
          <Label htmlFor="middleName">Middle Name</Label>
          <Input
            id="middleName"
            value={formData.personal.middleName}
            onChange={(e) => updateFormData('personal', 'middleName', e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="lastName">Last Name *</Label>
          <Input
            id="lastName"
            value={formData.personal.lastName}
            onChange={(e) => updateFormData('personal', 'lastName', e.target.value)}
            className={errors['personal.lastName'] ? 'border-red-500' : ''}
          />
          {errors['personal.lastName'] && (
            <p className="text-sm text-red-500 mt-1">{errors['personal.lastName']}</p>
          )}
        </div>
        <div>
          <Label htmlFor="preferredName">Preferred Name</Label>
          <Input
            id="preferredName"
            value={formData.personal.preferredName}
            onChange={(e) => updateFormData('personal', 'preferredName', e.target.value)}
            placeholder="What should we call you?"
          />
        </div>
        <div>
          <Label htmlFor="dateOfBirth">Date of Birth *</Label>
          <Input
            id="dateOfBirth"
            type="date"
            value={formData.personal.dateOfBirth}
            onChange={(e) => updateFormData('personal', 'dateOfBirth', e.target.value)}
            className={errors['personal.dateOfBirth'] ? 'border-red-500' : ''}
          />
          {errors['personal.dateOfBirth'] && (
            <p className="text-sm text-red-500 mt-1">{errors['personal.dateOfBirth']}</p>
          )}
        </div>
        <div>
          <Label htmlFor="ssn">Social Security Number *</Label>
          <Input
            id="ssn"
            type="password"
            value={formData.personal.ssn}
            onChange={(e) => updateFormData('personal', 'ssn', e.target.value)}
            placeholder="XXX-XX-XXXX"
            className={errors['personal.ssn'] ? 'border-red-500' : ''}
          />
          {errors['personal.ssn'] && (
            <p className="text-sm text-red-500 mt-1">{errors['personal.ssn']}</p>
          )}
        </div>
        <div>
          <Label htmlFor="ssnConfirm">Confirm SSN *</Label>
          <Input
            id="ssnConfirm"
            type="password"
            value={formData.personal.ssnConfirm}
            onChange={(e) => updateFormData('personal', 'ssnConfirm', e.target.value)}
            className={errors['personal.ssnConfirm'] ? 'border-red-500' : ''}
          />
          {errors['personal.ssnConfirm'] && (
            <p className="text-sm text-red-500 mt-1">{errors['personal.ssnConfirm']}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CONTACT INFO STEP
// ============================================================================

function ContactInfoStep({ formData, updateFormData, errors }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-1">Contact Information</h2>
        <p className="text-gray-500">How can we reach you?</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            value={formData.contact.email}
            disabled
            className="bg-gray-50"
          />
          <p className="text-xs text-gray-500 mt-1">Your work email from registration</p>
        </div>
        <div>
          <Label htmlFor="phone">Phone Number *</Label>
          <Input
            id="phone"
            value={formData.contact.phone}
            onChange={(e) => updateFormData('contact', 'phone', e.target.value)}
            placeholder="(555) 123-4567"
            className={errors['contact.phone'] ? 'border-red-500' : ''}
          />
          {errors['contact.phone'] && (
            <p className="text-sm text-red-500 mt-1">{errors['contact.phone']}</p>
          )}
        </div>
      </div>

      <Separator />

      <div>
        <h3 className="font-medium text-gray-900 mb-4">Mailing Address</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="mailingAddress1">Street Address *</Label>
            <Input
              id="mailingAddress1"
              value={formData.contact.mailingAddress1}
              onChange={(e) => updateFormData('contact', 'mailingAddress1', e.target.value)}
              className={errors['contact.mailingAddress1'] ? 'border-red-500' : ''}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="mailingAddress2">Apt/Suite/Unit</Label>
            <Input
              id="mailingAddress2"
              value={formData.contact.mailingAddress2}
              onChange={(e) => updateFormData('contact', 'mailingAddress2', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="mailingCity">City *</Label>
            <Input
              id="mailingCity"
              value={formData.contact.mailingCity}
              onChange={(e) => updateFormData('contact', 'mailingCity', e.target.value)}
              className={errors['contact.mailingCity'] ? 'border-red-500' : ''}
            />
          </div>
          <div>
            <Label htmlFor="mailingState">State *</Label>
            <Select
              value={formData.contact.mailingState}
              onValueChange={(value) => updateFormData('contact', 'mailingState', value)}
            >
              <SelectTrigger className={errors['contact.mailingState'] ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((state) => (
                  <SelectItem key={state.value} value={state.value}>
                    {state.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="mailingZip">ZIP Code *</Label>
            <Input
              id="mailingZip"
              value={formData.contact.mailingZip}
              onChange={(e) => updateFormData('contact', 'mailingZip', e.target.value)}
              className={errors['contact.mailingZip'] ? 'border-red-500' : ''}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// EMERGENCY CONTACT STEP
// ============================================================================

function EmergencyContactStep({ formData, updateFormData, errors }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-1">Emergency Contact</h2>
        <p className="text-gray-500">Who should we contact in case of emergency?</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Primary Contact *</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="primaryName">Full Name *</Label>
            <Input
              id="primaryName"
              value={formData.emergency.primaryName}
              onChange={(e) => updateFormData('emergency', 'primaryName', e.target.value)}
              className={errors['emergency.primaryName'] ? 'border-red-500' : ''}
            />
          </div>
          <div>
            <Label htmlFor="primaryRelationship">Relationship *</Label>
            <Select
              value={formData.emergency.primaryRelationship}
              onValueChange={(value) => updateFormData('emergency', 'primaryRelationship', value)}
            >
              <SelectTrigger className={errors['emergency.primaryRelationship'] ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select relationship" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="spouse">Spouse</SelectItem>
                <SelectItem value="partner">Partner</SelectItem>
                <SelectItem value="parent">Parent</SelectItem>
                <SelectItem value="sibling">Sibling</SelectItem>
                <SelectItem value="child">Child</SelectItem>
                <SelectItem value="friend">Friend</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="primaryPhone">Phone Number *</Label>
            <Input
              id="primaryPhone"
              value={formData.emergency.primaryPhone}
              onChange={(e) => updateFormData('emergency', 'primaryPhone', e.target.value)}
              className={errors['emergency.primaryPhone'] ? 'border-red-500' : ''}
            />
          </div>
          <div>
            <Label htmlFor="primaryEmail">Email (Optional)</Label>
            <Input
              id="primaryEmail"
              type="email"
              value={formData.emergency.primaryEmail}
              onChange={(e) => updateFormData('emergency', 'primaryEmail', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Secondary Contact (Optional)</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="secondaryName">Full Name</Label>
            <Input
              id="secondaryName"
              value={formData.emergency.secondaryName}
              onChange={(e) => updateFormData('emergency', 'secondaryName', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="secondaryRelationship">Relationship</Label>
            <Select
              value={formData.emergency.secondaryRelationship}
              onValueChange={(value) => updateFormData('emergency', 'secondaryRelationship', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select relationship" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="spouse">Spouse</SelectItem>
                <SelectItem value="partner">Partner</SelectItem>
                <SelectItem value="parent">Parent</SelectItem>
                <SelectItem value="sibling">Sibling</SelectItem>
                <SelectItem value="child">Child</SelectItem>
                <SelectItem value="friend">Friend</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="secondaryPhone">Phone Number</Label>
            <Input
              id="secondaryPhone"
              value={formData.emergency.secondaryPhone}
              onChange={(e) => updateFormData('emergency', 'secondaryPhone', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="secondaryEmail">Email</Label>
            <Input
              id="secondaryEmail"
              type="email"
              value={formData.emergency.secondaryEmail}
              onChange={(e) => updateFormData('emergency', 'secondaryEmail', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// BANK/DIRECT DEPOSIT STEP
// ============================================================================

function BankInfoStep({ formData, updateFormData, errors }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-1">Direct Deposit</h2>
        <p className="text-gray-500">Set up your bank account for seamless pay deposits</p>
      </div>

      <Alert className="bg-blue-50 border-blue-200">
        <Shield className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Your banking information is encrypted and stored securely. We use bank-level 
          security to protect your data.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bank Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Account Type</Label>
            <RadioGroup
              value={formData.bank.accountType}
              onValueChange={(value) => updateFormData('bank', 'accountType', value)}
              className="flex gap-4 mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="checking" id="checking" />
                <Label htmlFor="checking" className="font-normal cursor-pointer">Checking</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="savings" id="savings" />
                <Label htmlFor="savings" className="font-normal cursor-pointer">Savings</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bankName">Bank Name</Label>
              <Input
                id="bankName"
                value={formData.bank.bankName}
                onChange={(e) => updateFormData('bank', 'bankName', e.target.value)}
                placeholder="e.g., Chase, Bank of America"
              />
            </div>
            <div>
              <Label htmlFor="routingNumber">Routing Number *</Label>
              <Input
                id="routingNumber"
                value={formData.bank.routingNumber}
                onChange={(e) => updateFormData('bank', 'routingNumber', e.target.value)}
                maxLength={9}
                className={errors['bank.routingNumber'] ? 'border-red-500' : ''}
              />
              {errors['bank.routingNumber'] && (
                <p className="text-sm text-red-500 mt-1">{errors['bank.routingNumber']}</p>
              )}
            </div>
            <div>
              <Label htmlFor="routingNumberConfirm">Confirm Routing Number *</Label>
              <Input
                id="routingNumberConfirm"
                value={formData.bank.routingNumberConfirm}
                onChange={(e) => updateFormData('bank', 'routingNumberConfirm', e.target.value)}
                maxLength={9}
                className={errors['bank.routingNumberConfirm'] ? 'border-red-500' : ''}
              />
            </div>
            <div>
              <Label htmlFor="accountNumber">Account Number *</Label>
              <Input
                id="accountNumber"
                value={formData.bank.accountNumber}
                onChange={(e) => updateFormData('bank', 'accountNumber', e.target.value)}
                className={errors['bank.accountNumber'] ? 'border-red-500' : ''}
              />
              {errors['bank.accountNumber'] && (
                <p className="text-sm text-red-500 mt-1">{errors['bank.accountNumber']}</p>
              )}
            </div>
            <div>
              <Label htmlFor="accountNumberConfirm">Confirm Account Number *</Label>
              <Input
                id="accountNumberConfirm"
                value={formData.bank.accountNumberConfirm}
                onChange={(e) => updateFormData('bank', 'accountNumberConfirm', e.target.value)}
                className={errors['bank.accountNumberConfirm'] ? 'border-red-500' : ''}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-start gap-3">
        <Checkbox
          id="consentToDirectDeposit"
          checked={formData.bank.consentToDirectDeposit}
          onCheckedChange={(checked) => updateFormData('bank', 'consentToDirectDeposit', checked)}
        />
        <Label htmlFor="consentToDirectDeposit" className="text-sm font-normal cursor-pointer">
          I authorize my employer to deposit my pay into the account(s) specified above. I understand 
          that this authorization will remain in effect until I provide written notice to cancel it.
          {errors['bank.consentToDirectDeposit'] && (
            <span className="text-red-500 block mt-1">{errors['bank.consentToDirectDeposit']}</span>
          )}
        </Label>
      </div>
    </div>
  );
}

// ============================================================================
// POLICIES STEP
// ============================================================================

function PoliciesStep({ completedPolicies, onAcknowledgePolicy, employee }) {
  const requiredPolicies = Object.values(POLICY_TEMPLATES).filter(p => p.required);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);

  const handlePolicyClick = (policy) => {
    setSelectedPolicy(policy);
    setShowPolicyDialog(true);
  };

  const handleAcknowledge = (data) => {
    onAcknowledgePolicy(selectedPolicy.id, data);
    setShowPolicyDialog(false);
    setSelectedPolicy(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-1">Company Policies</h2>
        <p className="text-gray-500">Please review and acknowledge the following policies</p>
      </div>

      <Alert className="bg-amber-50 border-amber-200">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          You must read and acknowledge all required policies to continue.
          Take your time to review each policy carefully.
        </AlertDescription>
      </Alert>

      <div className="space-y-3">
        {requiredPolicies.map((policy) => {
          const isComplete = completedPolicies.includes(policy.id);

          return (
            <Card 
              key={policy.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                isComplete ? 'border-green-200 bg-green-50' : ''
              }`}
              onClick={() => !isComplete && handlePolicyClick(policy)}
            >
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isComplete ? (
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    ) : (
                      <FileText className="h-6 w-6 text-gray-400" />
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{policy.title}</p>
                      <p className="text-sm text-gray-500">{policy.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
                      Required
                    </Badge>
                    {!isComplete && (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={showPolicyDialog} onOpenChange={setShowPolicyDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedPolicy && (
            <PolicyAcknowledgment
              policy={selectedPolicy}
              employee={employee}
              onAcknowledge={handleAcknowledge}
              onCancel={() => setShowPolicyDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// MAIN COMPLIANCE ONBOARDING WIZARD COMPONENT
// ============================================================================

export default function ComplianceOnboardingWizard() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Compliance form states
  const [i9Complete, setI9Complete] = useState(false);
  const [w4Complete, setW4Complete] = useState(false);
  const [de4Complete, setDe4Complete] = useState(false);
  const [wtpnComplete, setWtpnComplete] = useState(false);
  const [noticesComplete, setNoticesComplete] = useState(false);
  const [completedPolicies, setCompletedPolicies] = useState([]);

  // Form data state
  const [formData, setFormData] = useState({
    personal: {
      firstName: user?.profile?.firstName || user?.name?.split(' ')[0] || '',
      middleName: '',
      lastName: user?.profile?.lastName || user?.name?.split(' ').slice(1).join(' ') || '',
      preferredName: '',
      dateOfBirth: '',
      ssn: '',
      ssnConfirm: '',
    },
    contact: {
      email: user?.email || '',
      phone: user?.profile?.phone || '',
      mailingAddress1: '',
      mailingAddress2: '',
      mailingCity: '',
      mailingState: '',
      mailingZip: '',
    },
    emergency: {
      primaryName: '',
      primaryRelationship: '',
      primaryPhone: '',
      primaryEmail: '',
      secondaryName: '',
      secondaryRelationship: '',
      secondaryPhone: '',
      secondaryEmail: '',
    },
    bank: {
      accountType: 'checking',
      routingNumber: '',
      routingNumberConfirm: '',
      accountNumber: '',
      accountNumberConfirm: '',
      bankName: '',
      consentToDirectDeposit: false,
    },
  });

  // Employee object for forms
  const employee = useMemo(() => ({
    id: user?.id,
    email: user?.email,
    firstName: formData.personal.firstName || user?.name?.split(' ')[0] || '',
    lastName: formData.personal.lastName || user?.name?.split(' ').slice(1).join(' ') || '',
    dateOfBirth: formData.personal.dateOfBirth,
    ssn: formData.personal.ssn,
    address: formData.contact.mailingAddress1,
    city: formData.contact.mailingCity,
    state: formData.contact.mailingState,
    zip: formData.contact.mailingZip,
    hireDate: new Date().toISOString().split('T')[0],
  }), [user, formData]);

  // Employer object for WTPN
  const employer = useMemo(() => ({
    name: 'SpaceHR Technologies Inc.',
    dba: '',
    address: '123 Innovation Drive',
    city: 'San Francisco',
    state: 'CA',
    zip: '94105',
    phone: '(415) 555-0100',
    fein: 'XX-XXXXXXX',
    workersCompCarrier: 'State Fund Insurance',
    workersCompPhone: '(800) 555-0199',
    workersCompPolicy: 'WC-123456789',
    payDay: 'Friday',
  }), []);

  // Update form data helper
  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Clear error
    if (errors[`${section}.${field}`]) {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[`${section}.${field}`];
        return updated;
      });
    }
  };

  // Validate current step
  const validateStep = () => {
    const step = ONBOARDING_STEPS[currentStep];
    const newErrors = {};

    switch (step.id) {
      case 'personal':
        if (!formData.personal.firstName?.trim()) newErrors['personal.firstName'] = 'First name is required';
        if (!formData.personal.lastName?.trim()) newErrors['personal.lastName'] = 'Last name is required';
        if (!formData.personal.dateOfBirth) newErrors['personal.dateOfBirth'] = 'Date of birth is required';
        if (!formData.personal.ssn?.trim()) newErrors['personal.ssn'] = 'SSN is required';
        if (formData.personal.ssn !== formData.personal.ssnConfirm) newErrors['personal.ssnConfirm'] = 'SSN does not match';
        break;

      case 'contact':
        if (!formData.contact.phone?.trim()) newErrors['contact.phone'] = 'Phone number is required';
        if (!formData.contact.mailingAddress1?.trim()) newErrors['contact.mailingAddress1'] = 'Address is required';
        if (!formData.contact.mailingCity?.trim()) newErrors['contact.mailingCity'] = 'City is required';
        if (!formData.contact.mailingState) newErrors['contact.mailingState'] = 'State is required';
        if (!formData.contact.mailingZip?.trim()) newErrors['contact.mailingZip'] = 'ZIP code is required';
        break;

      case 'emergency':
        if (!formData.emergency.primaryName?.trim()) newErrors['emergency.primaryName'] = 'Emergency contact name is required';
        if (!formData.emergency.primaryPhone?.trim()) newErrors['emergency.primaryPhone'] = 'Phone number is required';
        if (!formData.emergency.primaryRelationship) newErrors['emergency.primaryRelationship'] = 'Relationship is required';
        break;

      case 'i9':
        if (!i9Complete) newErrors['i9'] = 'Please complete Form I-9';
        break;

      case 'w4':
        if (!w4Complete) newErrors['w4'] = 'Please complete Form W-4';
        break;

      case 'de4':
        if (!de4Complete) newErrors['de4'] = 'Please complete Form DE-4';
        break;

      case 'wtpn':
        if (!wtpnComplete) newErrors['wtpn'] = 'Please acknowledge the Wage Theft Prevention Notice';
        break;

      case 'notices':
        if (!noticesComplete) newErrors['notices'] = 'Please acknowledge all California notices';
        break;

      case 'policies':
        const requiredPolicies = Object.values(POLICY_TEMPLATES).filter(p => p.required);
        if (completedPolicies.length < requiredPolicies.length) {
          newErrors['policies'] = 'Please acknowledge all required policies';
        }
        break;

      case 'bank':
        if (!formData.bank.routingNumber?.trim()) newErrors['bank.routingNumber'] = 'Routing number is required';
        if (formData.bank.routingNumber !== formData.bank.routingNumberConfirm) {
          newErrors['bank.routingNumberConfirm'] = 'Routing numbers do not match';
        }
        if (!formData.bank.accountNumber?.trim()) newErrors['bank.accountNumber'] = 'Account number is required';
        if (formData.bank.accountNumber !== formData.bank.accountNumberConfirm) {
          newErrors['bank.accountNumberConfirm'] = 'Account numbers do not match';
        }
        if (!formData.bank.consentToDirectDeposit) {
          newErrors['bank.consentToDirectDeposit'] = 'You must consent to direct deposit';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step
  const handleNext = () => {
    if (validateStep()) {
      const currentStepId = ONBOARDING_STEPS[currentStep].id;
      if (!completedSteps.includes(currentStepId)) {
        setCompletedSteps([...completedSteps, currentStepId]);
      }
      if (currentStep < ONBOARDING_STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle step click from sidebar
  const handleStepClick = (index) => {
    setCurrentStep(index);
  };

  // Handle I-9 complete
  const handleI9Complete = async (data) => {
    try {
      await db.i9Submissions.add({
        id: `i9_${user.id}_${Date.now()}`,
        userId: user.id,
        ...data,
        createdAt: new Date().toISOString(),
      });
      setI9Complete(true);
      toast.success('Form I-9 Section 1 completed!');
    } catch (error) {
      console.error('Error saving I-9:', error);
      toast.error('Failed to save I-9');
    }
  };

  // Handle W-4 complete
  const handleW4Complete = async (data) => {
    try {
      await db.formSubmissions.add({
        id: `w4_${user.id}_${Date.now()}`,
        formType: 'W-4',
        userId: user.id,
        ...data,
        createdAt: new Date().toISOString(),
      });
      setW4Complete(true);
      toast.success('Form W-4 completed!');
    } catch (error) {
      console.error('Error saving W-4:', error);
      toast.error('Failed to save W-4');
    }
  };

  // Handle DE-4 complete
  const handleDE4Complete = async (data) => {
    try {
      await db.formSubmissions.add({
        id: `de4_${user.id}_${Date.now()}`,
        formType: 'DE-4',
        userId: user.id,
        ...data,
        createdAt: new Date().toISOString(),
      });
      setDe4Complete(true);
      toast.success('Form DE-4 completed!');
    } catch (error) {
      console.error('Error saving DE-4:', error);
      toast.error('Failed to save DE-4');
    }
  };

  // Handle WTPN complete
  const handleWTPNComplete = async (data) => {
    try {
      await db.formSubmissions.add({
        id: `wtpn_${user.id}_${Date.now()}`,
        formType: 'WTPN',
        userId: user.id,
        ...data,
        createdAt: new Date().toISOString(),
      });
      setWtpnComplete(true);
      toast.success('Wage Theft Prevention Notice acknowledged!');
    } catch (error) {
      console.error('Error saving WTPN:', error);
      toast.error('Failed to save WTPN');
    }
  };

  // Handle CA Notices complete
  const handleNoticesComplete = async (data) => {
    try {
      await db.formSubmissions.add({
        id: `canotices_${user.id}_${Date.now()}`,
        formType: 'CA_NOTICES',
        userId: user.id,
        ...data,
        createdAt: new Date().toISOString(),
      });
      setNoticesComplete(true);
      toast.success('California notices acknowledged!');
    } catch (error) {
      console.error('Error saving CA notices:', error);
      toast.error('Failed to save notices');
    }
  };

  // Handle policy acknowledgment
  const handlePolicyAcknowledge = async (policyId, data) => {
    try {
      await db.policyAcknowledgments.add({
        id: `ack_${policyId}_${user.id}`,
        policyId,
        userId: user.id,
        status: 'acknowledged',
        acknowledgedAt: new Date().toISOString(),
        signature: data.signature,
      });
      setCompletedPolicies([...completedPolicies, policyId]);
      toast.success('Policy acknowledged!');
    } catch (error) {
      console.error('Error saving policy ack:', error);
      toast.error('Failed to save acknowledgment');
    }
  };

  // Handle final completion
  const handleComplete = async () => {
    setSaving(true);
    try {
      // Update user profile
      await db.users.update(user.id, {
        name: `${formData.personal.firstName} ${formData.personal.lastName}`,
        onboardingComplete: true,
        profile: {
          ...user.profile,
          firstName: formData.personal.firstName,
          lastName: formData.personal.lastName,
          dateOfBirth: formData.personal.dateOfBirth,
          phone: formData.contact.phone,
          address: {
            address1: formData.contact.mailingAddress1,
            address2: formData.contact.mailingAddress2,
            city: formData.contact.mailingCity,
            state: formData.contact.mailingState,
            zip: formData.contact.mailingZip,
          },
          emergencyContacts: [
            {
              name: formData.emergency.primaryName,
              relationship: formData.emergency.primaryRelationship,
              phone: formData.emergency.primaryPhone,
              email: formData.emergency.primaryEmail,
              isPrimary: true,
            },
          ].filter(c => c.name),
        },
        updatedAt: Date.now(),
      });

      // Save bank info
      await db.employeeBankAccounts.add({
        id: `bank_${user.id}_${Date.now()}`,
        userId: user.id,
        bankName: formData.bank.bankName,
        accountType: formData.bank.accountType,
        routingNumber: formData.bank.routingNumber,
        accountNumber: formData.bank.accountNumber,
        priority: 1,
        status: 'active',
        createdAt: Date.now(),
      });

      // Update auth store
      updateUser({ ...user, onboardingComplete: true });

      toast.success('Onboarding completed successfully!');
      navigate('/employee');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error('Failed to complete onboarding');
    } finally {
      setSaving(false);
    }
  };

  // Render current step content
  const renderStepContent = () => {
    const step = ONBOARDING_STEPS[currentStep];

    switch (step.id) {
      case 'welcome':
        return <WelcomeStep employee={employee} onStart={handleNext} />;
      
      case 'personal':
        return <PersonalInfoStep formData={formData} updateFormData={updateFormData} errors={errors} />;
      
      case 'contact':
        return <ContactInfoStep formData={formData} updateFormData={updateFormData} errors={errors} />;
      
      case 'emergency':
        return <EmergencyContactStep formData={formData} updateFormData={updateFormData} errors={errors} />;
      
      case 'i9':
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">Form I-9: Employment Eligibility</h2>
              <p className="text-gray-500">Verify your identity and work authorization</p>
            </div>
            {i9Complete ? (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Form I-9 Section 1 completed. Section 2 will be completed by HR on your first day.
                </AlertDescription>
              </Alert>
            ) : (
              <FormI9
                employee={employee}
                onSubmit={handleI9Complete}
              />
            )}
          </div>
        );
      
      case 'w4':
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">Form W-4: Federal Tax Withholding</h2>
              <p className="text-gray-500">Set your federal income tax withholding</p>
            </div>
            {w4Complete ? (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Form W-4 completed successfully.
                </AlertDescription>
              </Alert>
            ) : (
              <FormW4
                employee={employee}
                onSubmit={handleW4Complete}
              />
            )}
          </div>
        );
      
      case 'de4':
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">Form DE-4: California State Tax</h2>
              <p className="text-gray-500">Set your California state tax withholding</p>
            </div>
            {de4Complete ? (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Form DE-4 completed successfully.
                </AlertDescription>
              </Alert>
            ) : (
              <FormDE4
                employee={employee}
                onSubmit={handleDE4Complete}
              />
            )}
          </div>
        );
      
      case 'wtpn':
        return (
          <div className="space-y-4">
            {wtpnComplete ? (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Wage Theft Prevention Notice acknowledged.
                </AlertDescription>
              </Alert>
            ) : (
              <WageTheftNotice
                employee={employee}
                employer={employer}
                onSubmit={handleWTPNComplete}
              />
            )}
          </div>
        );
      
      case 'notices':
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">California Workplace Notices</h2>
              <p className="text-gray-500">Review and acknowledge required California employment notices</p>
            </div>
            {noticesComplete ? (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  All California notices acknowledged.
                </AlertDescription>
              </Alert>
            ) : (
              <CaliforniaNoticesViewer
                employee={employee}
                onComplete={handleNoticesComplete}
              />
            )}
          </div>
        );
      
      case 'policies':
        return (
          <PoliciesStep
            completedPolicies={completedPolicies}
            onAcknowledgePolicy={handlePolicyAcknowledge}
            employee={employee}
          />
        );
      
      case 'bank':
        return <BankInfoStep formData={formData} updateFormData={updateFormData} errors={errors} />;
      
      case 'complete':
        return <CompletionStep employee={employee} onFinish={handleComplete} />;
      
      default:
        return null;
    }
  };

  const step = ONBOARDING_STEPS[currentStep];
  const showNavigation = step.id !== 'welcome' && step.id !== 'complete';

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex gap-6">
          {/* Sidebar Progress */}
          <StepProgressIndicator
            steps={ONBOARDING_STEPS}
            currentStep={currentStep}
            completedSteps={completedSteps}
            onStepClick={handleStepClick}
          />

          {/* Main Content */}
          <div className="flex-1">
            {/* Mobile Progress */}
            <MobileStepIndicator
              steps={ONBOARDING_STEPS}
              currentStep={currentStep}
              completedSteps={completedSteps}
            />

            {/* Content Card */}
            <Card>
              <CardContent className="pt-6">
                {renderStepContent()}
              </CardContent>

              {/* Navigation */}
              {showNavigation && (
                <CardFooter className="flex justify-between border-t pt-6">
                  <Button
                    variant="outline"
                    onClick={handlePrevious}
                    disabled={currentStep === 0}
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Previous
                  </Button>
                  <Button onClick={handleNext} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        Next
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardFooter>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
